import React, { useState, useEffect, useCallback } from 'react';
import { host } from '../script/variables';
import '../style/HoldOrders.css';

/* ── helpers ─────────────────────────────────────────────── */
const fmt = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(n || 0);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  }) : '—';

/* ── PaymentModal ────────────────────────────────────────── */
const PaymentModal = ({ order, onClose, onConfirm }) => {
  const [cashAmount,    setCashAmount]    = useState(order.totalPrice);
  const [digitalAmount, setDigitalAmount] = useState(0);
  const [digitalMethod, setDigitalMethod] = useState('upi');

  const handleCashChange = (v) => {
    const c = Math.min(Number(v), order.totalPrice);
    setCashAmount(c);
    setDigitalAmount(order.totalPrice - c);
  };
  const handleDigitalChange = (v) => {
    const d = Math.min(Number(v), order.totalPrice);
    setDigitalAmount(d);
    setCashAmount(order.totalPrice - d);
  };

  const valid = Math.abs((cashAmount + digitalAmount) - order.totalPrice) < 0.01;

  return (
    <div className="ho-modal-backdrop">
      <div className="ho-modal">
        <div className="ho-modal-header">
          <h3>Mark as Paid</h3>
          <button className="ho-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="ho-modal-total">
          <span>Total Due</span>
          <strong>{fmt(order.totalPrice)}</strong>
        </div>

        <div className="ho-quick-split">
          <button onClick={() => { setCashAmount(order.totalPrice); setDigitalAmount(0); }}>
            💵 Full Cash
          </button>
          <button onClick={() => { setCashAmount(0); setDigitalAmount(order.totalPrice); }}>
            📱 Full Digital
          </button>
        </div>

        <hr className="ho-divider" />

        <div className="ho-split-row">
          <div className="ho-field">
            <label>Cash (₹)</label>
            <input type="number" min="0" max={order.totalPrice}
              value={cashAmount} onChange={e => handleCashChange(e.target.value)} />
          </div>
          <div className="ho-field">
            <label>Digital (₹)</label>
            <input type="number" min="0" max={order.totalPrice}
              value={digitalAmount} onChange={e => handleDigitalChange(e.target.value)} />
          </div>
        </div>

        {digitalAmount > 0 && (
          <div className="ho-field">
            <label>Digital Method</label>
            <select value={digitalMethod} onChange={e => setDigitalMethod(e.target.value)}>
              <option value="upi">UPI</option>
              <option value="cc">Credit Card</option>
              <option value="dc">Debit Card</option>
              <option value="netbanking">Net Banking</option>
              <option value="wallet">Wallet</option>
            </select>
          </div>
        )}

        {!valid && (
          <p className="ho-error">Cash + Digital must equal {fmt(order.totalPrice)}</p>
        )}

        <div className="ho-modal-actions">
          <button className="ho-btn-confirm" disabled={!valid}
            onClick={() => onConfirm({ cashAmount, digitalAmount, digitalMethod })}>
            ✓ Confirm Payment
          </button>
          <button className="ho-btn-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

/* ── ReminderModal ───────────────────────────────────────── */
const ReminderModal = ({ orders, targetOrder, onClose, onConfirm, sending }) => {
  const isSingle  = !!targetOrder;
  const count     = isSingle ? 1 : orders.length;
  const hasEmails = isSingle
    ? !!targetOrder.customer?.email
    : orders.some(o => o.customer?.email);

  return (
    <div className="ho-modal-backdrop">
      <div className="ho-modal">
        <div className="ho-modal-header">
          <h3>Send Payment Reminder</h3>
          <button className="ho-modal-close" onClick={onClose}>✕</button>
        </div>
        <p className="ho-reminder-desc">
          {isSingle
            ? `Send a payment reminder to ${targetOrder.customer?.name || 'this customer'} for ${fmt(targetOrder.totalPrice)}.`
            : `Send reminders to all ${count} hold order customer${count !== 1 ? 's' : ''}.`}
        </p>
        {!hasEmails && (
          <p className="ho-warning">⚠ No email addresses found — SMS/push will be used if configured.</p>
        )}
        <div className="ho-modal-actions">
          <button className="ho-btn-confirm" disabled={sending} onClick={onConfirm}>
            {sending ? '⏳ Sending…' : `📨 Send ${isSingle ? 'Reminder' : `${count} Reminders`}`}
          </button>
          <button className="ho-btn-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

/* ── HoldOrders main ─────────────────────────────────────── */
const HoldOrders = ({ cafeid, startDate, endDate }) => {
  const [orders,         setOrders]         = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');
  const [toast,          setToast]          = useState(null);

  // modals
  const [payModal,       setPayModal]       = useState(null);   // order object
  const [reminderModal,  setReminderModal]  = useState(null);   // { isSingle, order? }
  const [sending,        setSending]        = useState(false);

  // expanded rows
  const [expanded,       setExpanded]       = useState({});

  const token = () => localStorage.getItem('partnertoken');

  /* ── fetch hold orders ── */
  const fetchHoldOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${host}/transtats/hold-orders`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token() },
        body:    JSON.stringify({ cafeid, startDate, endDate })
      });
      const json = await res.json();
      if (json.success) setOrders(json.data);
      else setError(json.message || 'Failed to load hold orders');
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [cafeid, startDate, endDate]);

  useEffect(() => { fetchHoldOrders(); }, [fetchHoldOrders]);

  /* ── toast helper ── */
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  /* ── mark paid ── */
  const handleMarkPaid = async ({ cashAmount, digitalAmount, digitalMethod }) => {
    try {
      const res  = await fetch(`${host}/transtats/hold-orders/mark-paid`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token() },
        body:    JSON.stringify({
          orderId: payModal._id, cafeid, cashAmount, digitalAmount, digitalMethod
        })
      });
      const json = await res.json();
      if (json.success) {
        setOrders(prev => prev.filter(o => o._id !== payModal._id));
        showToast('Payment recorded successfully ✓');
      } else {
        showToast(json.message || 'Failed to record payment', 'error');
      }
    } catch {
      showToast('Network error', 'error');
    } finally {
      setPayModal(null);
    }
  };

  /* ── send reminder ── */
  const handleSendReminder = async () => {
    setSending(true);
    const isSingle  = !!reminderModal.order;
    const orderIds  = isSingle ? [reminderModal.order._id] : [];
    try {
      const res  = await fetch(`${host}/transtats/hold-orders/send-reminder`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token() },
        body:    JSON.stringify({ cafeid, orderIds })
      });
      const json = await res.json();
      if (json.success) {
        showToast(`Reminder sent to ${json.sent} customer(s) ✓`);
      } else {
        showToast(json.message || 'Failed to send reminder', 'error');
      }
    } catch {
      showToast('Network error', 'error');
    } finally {
      setSending(false);
      setReminderModal(null);
    }
  };

  const toggleExpand = (id) =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  /* ── empty / loading states ── */
  if (loading) return (
    <div className="ho-container">
      <div className="ho-loading"><div className="ho-spinner" /><span>Loading hold orders…</span></div>
    </div>
  );

  if (error) return (
    <div className="ho-container">
      <p className="ho-error-msg">⚠ {error}</p>
    </div>
  );

  return (
    <div className="ho-container">
      {/* ── header ── */}
      <div className="ho-header">
        <div className="ho-header-left">
          <h2>🔒 Due Payments</h2>
          <span className="ho-count-badge">{orders.length}</span>
        </div>
        {orders.length > 0 && (
          <button
            className="ho-btn-remind-all"
            onClick={() => setReminderModal({ isSingle: false })}
          >
            📨 Remind All ({orders.length})
          </button>
        )}
      </div>

      {orders.length === 0 ? (
        <div className="ho-empty">
          <span>🎉</span>
          <p>No due orders in this period</p>
        </div>
      ) : (
        <div className="ho-table-wrap">
          <table className="ho-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <React.Fragment key={order._id}>
                  <tr className="ho-row">
                    {/* Order ID + expand toggle */}
                    <td>
                      <button
                        className="ho-order-id-btn"
                        onClick={() => toggleExpand(order._id)}
                        title="View items"
                      >
                        <span className="ho-expand-icon">{expanded[order._id] ? '▾' : '▸'}</span>
                        <span className="ho-order-id">{order.orderId.slice(-8).toUpperCase()}</span>
                      </button>
                      <div className="ho-item-count">{order.items.length} item{order.items.length !== 1 ? 's' : ''}</div>
                    </td>

                    {/* Customer */}
                    <td>
                      <div className="ho-customer-name">{order.customer.name || <span className="ho-blank">—</span>}</div>
                      <div className="ho-customer-meta">{order.customer.email || <span className="ho-blank">no email</span>}</div>
                      <div className="ho-customer-meta">{order.customer.phone || <span className="ho-blank">no phone</span>}</div>
                    </td>

                    {/* Amount */}
                    <td>
                      <span className="ho-amount">{fmt(order.totalPrice)}</span>
                    </td>

                    {/* Date */}
                    <td>
                      <span className="ho-date">{fmtDate(order.placedAt)}</span>
                    </td>

                    {/* Delivery status */}
                    <td>
                      <span className={`ho-delivery-tag ho-d-${order.deliveryStatus?.toLowerCase()}`}>
                        {order.deliveryStatus}
                      </span>
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="ho-actions">
                        <button
                          className="ho-action-btn ho-pay-btn"
                          title="Mark as paid"
                          onClick={() => setPayModal(order)}
                        >
                          ✓ Paid
                        </button>
                        <button
                          className="ho-action-btn ho-remind-btn"
                          title="Send reminder"
                          onClick={() => setReminderModal({ isSingle: true, order })}
                        >
                          📨
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded items row */}
                  {expanded[order._id] && (
                    <tr className="ho-items-row">
                      <td colSpan={6}>
                        <div className="ho-items-wrap">
                          <table className="ho-items-table">
                            <thead>
                              <tr>
                                <th>Item</th>
                                <th>Qty</th>
                                <th>Price</th>
                                <th>Discount</th>
                                <th>Subtotal</th>
                              </tr>
                            </thead>
                            <tbody>
                              {order.items.map((item, i) => {
                                const sub = (item.price - (item.discount || 0)) * item.quantity;
                                return (
                                  <tr key={i}>
                                    <td>{item.item}</td>
                                    <td>{item.quantity}</td>
                                    <td>₹{item.price}</td>
                                    <td>{item.discount ? `₹${item.discount}` : '—'}</td>
                                    <td>₹{sub.toFixed(2)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          {order.specialInstructions && (
                            <p className="ho-special">📝 {order.specialInstructions}</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modals ── */}
      {payModal && (
        <PaymentModal
          order={payModal}
          onClose={() => setPayModal(null)}
          onConfirm={handleMarkPaid}
        />
      )}

      {reminderModal && (
        <ReminderModal
          orders={orders}
          targetOrder={reminderModal.isSingle ? reminderModal.order : null}
          onClose={() => setReminderModal(null)}
          onConfirm={handleSendReminder}
          sending={sending}
        />
      )}

      {/* ── Toast ── */}
      {toast && (
        <div className={`ho-toast ho-toast-${toast.type}`}>{toast.msg}</div>
      )}
    </div>
  );
};

export default HoldOrders;